import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Workflow as WorkflowIcon, RotateCcw, ChevronRight } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';

interface ExecutionSummary {
  id: string;
  chainId: string;
  chainVersion: number;
  status: string;
  startedAt: string | null;
  completedAt: string | null;
  nextResumeAt: string | null;
}

interface ExecutionDetail {
  execution: ExecutionSummary;
  triggerPayload: string | null;
  errorSummary: string | null;
  steps: Array<{
    id: string;
    chainStepId: string;
    attempt: number;
    status: string;
    startedAt: string | null;
    completedAt: string | null;
    errorSummary: string | null;
  }>;
}

const STATUS_CHOICES = ['ALL', 'PENDING', 'RUNNING', 'SUSPENDED_WAIT', 'SUCCEEDED', 'FAILED'] as const;

const STATUS_BADGE: Record<string, string> = {
  PENDING: 'bg-muted text-foreground',
  RUNNING: 'bg-blue-900/40 text-blue-200',
  SUSPENDED_WAIT: 'bg-amber-900/40 text-amber-200',
  SUCCEEDED: 'bg-emerald-900/40 text-emerald-200',
  FAILED: 'bg-rose-900/40 text-rose-200',
};

/**
 * Story 52.3 — DLQ surface for the workflow engine. Lists executions
 * with a status filter (defaulting to FAILED so ops can find broken
 * runs fast) and exposes a retry button.
 */
export function WorkflowRunsPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<ExecutionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<typeof STATUS_CHOICES[number]>('FAILED');
  const [refreshToken, setRefreshToken] = useState(0);
  const [selected, setSelected] = useState<ExecutionDetail | null>(null);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    const params: Record<string, string> = { limit: '100' };
    if (statusFilter !== 'ALL') params.status = statusFilter;
    apiClient
      .get<ExecutionSummary[]>('/workflow-executions', { params })
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [statusFilter, refreshToken]);

  async function openDetail(id: string) {
    try {
      const { data } = await apiClient.get<ExecutionDetail>(`/workflow-executions/${id}`);
      setSelected(data);
    } catch {
      setToast(t('workflowRuns.error_load_detail'));
    }
  }

  async function handleRetry(id: string) {
    setRetrying(id);
    try {
      await apiClient.post(`/workflow-executions/${id}/retry`);
      setToast(t('workflowRuns.retry_queued'));
      setRefreshToken((x) => x + 1);
    } catch (err) {
      const detail = isAxiosError(err)
        ? (err.response?.data as { detail?: string } | undefined)?.detail
        : null;
      setToast(detail ?? t('workflowRuns.retry_failed'));
    } finally {
      setRetrying(null);
      setTimeout(() => setToast(null), 3000);
    }
  }

  return (
    <div className="space-y-4 p-6">
      {toast && (
        <div className="fixed right-6 top-20 z-50 rounded-lg border bg-card px-4 py-2 text-sm shadow-lg">
          {toast}
        </div>
      )}

      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <WorkflowIcon className="h-5 w-5 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold">{t('workflowRuns.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('workflowRuns.subtitle')}</p>
          </div>
        </div>
        <div className="flex gap-1">
          {STATUS_CHOICES.map((choice) => (
            <Button
              key={choice}
              size="sm"
              variant={statusFilter === choice ? 'default' : 'outline'}
              onClick={() => setStatusFilter(choice)}
            >
              {choice}
            </Button>
          ))}
        </div>
      </header>

      <Card className="overflow-hidden p-0">
        {loading ? (
          <p className="px-6 py-6 text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : rows.length === 0 ? (
          <p className="px-6 py-6 text-sm text-muted-foreground">
            {t(`workflowRuns.empty_${statusFilter.toLowerCase()}`, {
              defaultValue: t('workflowRuns.empty_default'),
            })}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left">{t('workflowRuns.col.status')}</th>
                <th className="px-4 py-2 text-left">{t('workflowRuns.col.chain')}</th>
                <th className="px-4 py-2 text-left">{t('workflowRuns.col.started')}</th>
                <th className="px-4 py-2 text-left">{t('workflowRuns.col.completed')}</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t">
                  <td className="px-4 py-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        STATUS_BADGE[row.status] ?? 'bg-muted'
                      }`}
                    >
                      {row.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                    {row.chainId.slice(0, 8)}…@v{row.chainVersion}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {row.startedAt ? new Date(row.startedAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {row.completedAt ? new Date(row.completedAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs"
                        onClick={() => openDetail(row.id)}
                      >
                        <ChevronRight className="h-3.5 w-3.5" />
                      </Button>
                      {row.status === 'FAILED' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs"
                          onClick={() => handleRetry(row.id)}
                          disabled={retrying === row.id}
                        >
                          <RotateCcw className="mr-1 h-3 w-3" />
                          {retrying === row.id ? t('workflowRuns.retrying') : t('workflowRuns.retry')}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {selected && (
        <RunDetailDialog detail={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function RunDetailDialog({
  detail,
  onClose,
}: {
  detail: ExecutionDetail;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-lg border bg-card p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">{t('workflowRuns.detail_title')}</h3>
          <Button size="sm" variant="ghost" onClick={onClose}>
            ×
          </Button>
        </div>
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('workflowRuns.col.status')} value={detail.execution.status} />
            <Field
              label={t('workflowRuns.col.chain')}
              value={`${detail.execution.chainId.slice(0, 8)}…@v${detail.execution.chainVersion}`}
            />
            <Field
              label={t('workflowRuns.col.started')}
              value={detail.execution.startedAt ? new Date(detail.execution.startedAt).toLocaleString() : '—'}
            />
            <Field
              label={t('workflowRuns.col.completed')}
              value={
                detail.execution.completedAt
                  ? new Date(detail.execution.completedAt).toLocaleString()
                  : '—'
              }
            />
          </div>
          {detail.errorSummary && (
            <div className="rounded-md border border-rose-800 bg-rose-950/40 px-3 py-2 text-sm text-rose-200">
              <div className="font-semibold">{t('workflowRuns.error_label')}</div>
              <div className="mt-1 font-mono text-xs">{detail.errorSummary}</div>
            </div>
          )}
          {detail.triggerPayload && (
            <div>
              <div className="text-xs font-medium uppercase text-muted-foreground">
                {t('workflowRuns.payload_label')}
              </div>
              <pre className="mt-1 max-h-40 overflow-auto rounded-md bg-muted/40 px-3 py-2 text-xs">
                {detail.triggerPayload}
              </pre>
            </div>
          )}
          <div>
            <div className="mb-2 text-xs font-medium uppercase text-muted-foreground">
              {t('workflowRuns.steps_label')} ({detail.steps.length})
            </div>
            <ul className="divide-y rounded-md border bg-card">
              {detail.steps.map((step) => (
                <li
                  key={step.id}
                  className="flex items-center justify-between gap-3 px-3 py-2 text-xs"
                >
                  <div>
                    <div className="font-medium">
                      {step.chainStepId.slice(0, 8)}… (attempt {step.attempt})
                    </div>
                    {step.errorSummary && (
                      <div className="text-rose-300">{step.errorSummary}</div>
                    )}
                  </div>
                  <span
                    className={`rounded-full px-2 py-0.5 ${
                      STATUS_BADGE[step.status] ?? 'bg-muted'
                    }`}
                  >
                    {step.status}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm">{value}</div>
    </div>
  );
}
